import React, { useState, useEffect } from 'react';
import { Layout, Menu, Card, Statistic, Table, Space, Avatar, Badge, Button, Alert, Modal, message, Typography } from 'antd';
import { UserOutlined, DashboardOutlined, FileTextOutlined, PlusOutlined, EyeOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined, MessageOutlined, LogoutOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import SupportRequests from './SupportRequests';
import MyRequests from './MyRequests';
import SubmitTicket from './SubmitTicket';
import PersonalInformation from './PersonalInformation';
import ConnectedUsers from './ConnectedUsers';
import Verifications from './Verifications';
import './AdminPage.css';

const { Sider, Content } = Layout;
const { Text, Paragraph } = Typography;

const DashboardContent = () => {
  const [dataSource, setDataSource] = useState([]);
  const [stats, setStats] = useState({ total: 0, rejected: 0, approved: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const navigate = useNavigate();

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      console.log('Envoi de la requête vers /backend/admin_get_dashboard_data.php');
      const response = await fetch('/backend/admin_get_dashboard_data.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      console.log('Statut HTTP:', response.status);
      const text = await response.text();
      console.log('Réponse brute:', text);
      let data;
      try {
        data = JSON.parse(text);
        console.log('Données parsées:', data);
      } catch (e) {
        console.error('Erreur de parsing JSON:', e, 'Texte reçu:', text);
        throw new Error('Réponse non-JSON');
      }
      if (response.ok) {
        setDataSource(data.records);
        setStats(data.stats);
        setError(null);
      } else {
        if (response.status === 403) {
          console.log('Erreur 403: Session expirée');
          message.error('Session expirée. Veuillez vous reconnecter.');
          navigate('/adminlogin');
        } else {
          setError(data.error || `Erreur HTTP ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Erreur réseau:', error.message);
      setError(`Erreur réseau: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Confirmer la suppression',
      content: `Voulez-vous vraiment supprimer l'enregistrement ID ${record.id} ?`,
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          console.log(`Suppression de l'enregistrement ID ${record.id}`);
          const response = await fetch('/backend/admin_delete_support_record.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ id: record.id }),
          });
          console.log('Statut HTTP suppression:', response.status);
          const data = await response.json();
          console.log('Réponse suppression:', data);
          if (response.ok) {
            message.success('Enregistrement supprimé avec succès');
            setDataSource(dataSource.filter((item) => item.id !== record.id));
            setStats((prev) => ({
              ...prev,
              total: prev.total - 1,
              [record.status.toLowerCase()]: prev[record.status.toLowerCase()] - 1,
            }));
          } else {
            message.error(data.error || 'Erreur lors de la suppression');
          }
        } catch (error) {
          console.error('Erreur réseau lors de la suppression:', error);
          message.error('Erreur réseau lors de la suppression');
        }
      },
    });
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const columns = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} className="user-avatar" />
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Badge
          count={status}
          style={{
            backgroundColor: status === 'Approved' ? '#52c41a' : '#ff4d4f',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '12px',
          }}
        />
      ),
      width: 120,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            className="action-btn view-btn"
            onClick={() => handleView(record)}
          />
          <Button
            type="danger"
            icon={<DeleteOutlined />}
            className="action-btn delete-btn"
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
      width: 150,
    },
  ];

  return (
    <>
      <div className="header-section">
        <Space align="center">
          <Avatar size={48} icon={<UserOutlined />} className="header-avatar" />
          <div>
            <h1 className="greeting">Welcome back, NH GUIDE!</h1>
            <p className="sub-greeting">Let’s manage your support requests efficiently.</p>
          </div>
        </Space>
      </div>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 20 }} />}
      <div className="stats-container">
        <Card className="stat-card total">
          <Statistic
            title="Total Support Request"
            value={stats.total}
            valueStyle={{ color: '#fff', fontSize: '36px', fontWeight: 700 }}
            prefix={<FileTextOutlined className="stat-icon" />}
            style={{ textAlign: 'center' }}
          />
        </Card>
        <Card className="stat-card canceled">
          <Statistic
            title="Canceled Requests"
            value={stats.rejected}
            valueStyle={{ color: '#fff', fontSize: '36px', fontWeight: 700 }}
            prefix={<CloseCircleOutlined className="stat-icon" />}
            style={{ textAlign: 'center' }}
          />
        </Card>
        <Card className="stat-card approved">
          <Statistic
            title="Approved Requests"
            value={stats.approved}
            valueStyle={{ color: '#fff', fontSize: '36px', fontWeight: 700 }}
            prefix={<CheckCircleOutlined className="stat-icon" />}
            style={{ textAlign: 'center' }}
          />
        </Card>
      </div>
      <h2 className="section-title">Recent Support Requests</h2>
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        className="table-style"
        rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-even' : 'table-row-odd')}
        loading={loading}
      />
      <Modal
        title={`Détails de l'enregistrement ID ${selectedRecord?.id}`}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalOk}
        okText="Fermer"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {selectedRecord && (
          <div>
            <Paragraph><Text strong>Email:</Text> {selectedRecord.name}</Paragraph>
            <Paragraph><Text strong>Date:</Text> {selectedRecord.date}</Paragraph>
            <Paragraph><Text strong>Code postal:</Text> {selectedRecord.zip_code}</Paragraph>
            <Paragraph><Text strong>Ville:</Text> {selectedRecord.city}</Paragraph>
            <Paragraph>
              <Text strong>Besoins:</Text>
              {selectedRecord.needs.length > 0 ? (
                <ul>
                  {selectedRecord.needs.map((need, index) => (
                    <li key={index}>{need}</li>
                  ))}
                </ul>
              ) : (
                ' Aucun'
              )}
            </Paragraph>
            <Paragraph>
              <Text strong>Connexions:</Text>
              {selectedRecord.connections.length > 0 ? (
                <ul>
                  {selectedRecord.connections.map((connection, index) => (
                    <li key={index}>{connection}</li>
                  ))}
                </ul>
              ) : (
                ' Aucune'
              )}
            </Paragraph>
          </div>
        )}
      </Modal>
    </>
  );
};

const AdminPage = () => {
  const [activeView, setActiveView] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [fullNames, setFullNames] = useState('Name');
  const navigate = useNavigate();

  const fetchAdminProfile = async () => {
    try {
      console.log('Récupération du profil depuis /backend/admin_get_profile.php');
      const response = await fetch('https://app.nhguide.org/backend/admin_get_profile.php', {
        method: 'GET',
        credentials: 'include',
      });
      console.log('Statut HTTP:', response.status);
      const text = await response.text();
      console.log('Réponse brute:', text);
      let data;
      try {
        data = JSON.parse(text);
        console.log('Réponse:', data);
      } catch (e) {
        console.error('Erreur de parsing JSON:', e, 'Texte reçu:', text);
        throw new Error('Réponse non-JSON');
      }

      if (response.ok) {
        setFullNames(data.full_names || 'Name');
      } else {
        console.error('Erreur lors de la récupération du profil:', data.error);
        message.error('Erreur lors de la récupération du profil.');
      }
    } catch (error) {
      console.error('Erreur réseau:', error.message);
      message.error('Erreur réseau lors de la récupération du profil.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminProfile(); // Charger le profil directement
  }, []);

  const handleLogout = async () => {
    try {
      const response = await fetch('/backend/logoutadmin.php', {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        navigate('/adminlogin');
      }
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      navigate('/adminlogin');
    }
  };

  const menuItems = [
    { key: '1', icon: <UserOutlined />, label: fullNames },
    { key: '2', icon: <DashboardOutlined />, label: 'Dashboard' },
    { key: '3', icon: <FileTextOutlined />, label: 'Support Requests' },
    {
      key: '4',
      icon: <UserOutlined />,
      label: 'My Profile',
      children: [
        { key: '4-1', label: 'Connected users' },
        { key: '4-2', label: 'Personal informations' },
        { key: '4-3', label: 'Verifications' },
      ],
    },
    {
      key: '6',
      icon: <MessageOutlined />,
      label: 'Admin Support',
      children: [
        {
          key: '6-1',
          label: 'Talk to us',
          children: [
            { key: '6-1-1', label: 'Submit a ticket' },
            { key: '6-1-2', label: 'Live support' },
          ],
        },
        { key: '6-2', label: 'My request' },
      ],
    },
    { key: '5', icon: <PlusOutlined />, label: 'Add Items' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Déconnexion' },
  ];

  const handleMenuClick = ({ key }) => {
    if (key === '3') {
      setActiveView('support');
    } else if (key === '2') {
      setActiveView('dashboard');
    } else if (key === '6-1-1') {
      setActiveView('submitticket');
    } else if (key === '6-1-2') {
      alert('Live support selected');
    } else if (key === '6-2') {
      setActiveView('myrequests');
    } else if (key === '4-1') {
      setActiveView('connectedusers');
    } else if (key === '4-2') {
      setActiveView('personalinfo');
    } else if (key === '4-3') {
      setActiveView('verifications');
    } else if (key === 'logout') {
      handleLogout();
    }
  };

  if (loading) {
    return <div>Chargement...</div>;
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#e9ecef' }}>
      <Sider width={260} className="sider-style">
        <div className="logo">
          <h2>NH GUIDE</h2>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          defaultSelectedKeys={['2']}
          className="menu-style"
          onClick={handleMenuClick}
          items={menuItems}
        />
      </Sider>
      <Layout>
        <Content className="content-style">
          {activeView === 'dashboard' ? (
            <DashboardContent />
          ) : activeView === 'support' ? (
            <SupportRequests />
          ) : activeView === 'myrequests' ? (
            <MyRequests />
          ) : activeView === 'submitticket' ? (
            <SubmitTicket />
          ) : activeView === 'personalinfo' ? (
            <PersonalInformation />
          ) : activeView === 'connectedusers' ? (
            <ConnectedUsers />
          ) : activeView === 'verifications' ? (
            <Verifications />
          ) : null}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminPage;